package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/** Lifecycle of a moderation report submission, surfaced by the sheet. */
enum class ReportStatus { Idle, Sending, Sent, Failed }

/** One selectable report reason: [label] is localized display copy, [key] is the stable wire value. */
data class ReportReason(val label: String, val key: String)

/**
 * Reusable "report this" bottom sheet — a reason picker that feeds the moderation queue. **Copy-free**:
 * every string (title, reasons, confirmations, button labels) is passed in by the caller so each
 * surface (business, marketplace item, community post) supplies its own localized copy and its own
 * relevant reason set. The admin content-moderation queue consumes whatever `key` the caller submits.
 */
@Composable
fun ReportSheet(
    title: String,
    reasons: List<ReportReason>,
    status: ReportStatus,
    sentText: String,
    failedText: String,
    submitText: String,
    doneText: String,
    onSubmit: (reasonKey: String) -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
) {
    var selected by remember { mutableStateOf<String?>(null) }
    AppBottomSheet(onDismissRequest = onDismiss, modifier = modifier) {
        Column(
            modifier = Modifier.padding(AppTheme.spacing.lg),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            Text(
                text = title,
                style = AppTheme.typography.headlineMd,
                color = AppTheme.colors.onSurface,
            )
            if (status == ReportStatus.Sent) {
                Text(
                    text = sentText,
                    style = AppTheme.typography.bodyMd,
                    color = AppTheme.colors.primary,
                )
                AppButton(
                    text = doneText,
                    onClick = onDismiss,
                    modifier = Modifier.fillMaxWidth(),
                )
            } else {
                reasons.forEach { reason ->
                    AppChip(
                        label = reason.label,
                        selected = selected == reason.key,
                        onClick = { selected = reason.key },
                    )
                }
                if (status == ReportStatus.Failed) {
                    Text(
                        text = failedText,
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.onErrorContainer,
                    )
                }
                AppButton(
                    text = submitText,
                    onClick = { selected?.let(onSubmit) },
                    modifier = Modifier.fillMaxWidth(),
                    enabled = selected != null && status != ReportStatus.Sending,
                    loading = status == ReportStatus.Sending,
                    leadingIcon = "flag",
                )
            }
        }
    }
}
